"use client";

import React from "react";
import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
function PulsePostsConsumer() {
  const {
    posts,
    loading,
    error
  } = usePosts();
  if (loading) {
    return <div data-section-id="105594" className="grid grid-cols-1 lg:grid-cols-12 gap-8 animate-pulse">
        <div className="lg:col-span-7 h-[600px] bg-muted/10 rounded-sm"></div>
        <div className="lg:col-span-5 flex flex-col gap-8">
          <div className="h-[284px] bg-muted/10 rounded-sm"></div>
          <div className="h-[284px] bg-muted/10 rounded-sm"></div>
        </div>
      </div>;
  }
  if (error) {
    return <div className="text-center py-12 border border-primary/20 rounded-sm bg-primary/5">
        <p className="text-primary font-default">Unable to track the feed at this moment.</p>
      </div>;
  }
  if (!posts || posts.length === 0) {
    return <div className="text-center py-12 border border-border/20 rounded-sm bg-muted/5">
        <p className="text-muted-foreground font-sans">No new entries in the garage.</p>
      </div>;
  }
  const mainPost = posts[0];
  const secondaryPosts = posts.slice(1, 3);
  return <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
      {}
      {mainPost && <article className="lg:col-span-7 group relative h-[500px] sm:h-[600px] lg:h-[700px] rounded-sm overflow-hidden isolate">
          <img src={mainPost.featuredImage || "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1631141/img/magazine_main_feature.png"} alt={mainPost.title} className="absolute inset-0 w-full h-full object-cover grayscale-[0.6] group-hover:grayscale-0 transition-all duration-1000 ease-in-out scale-100 group-hover:scale-105 -z-10" />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/60 to-transparent -z-10" />
          
          <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-8 lg:p-12">
            <div className="bg-background/90 backdrop-blur-md border border-border/30 p-6 sm:p-8 lg:p-10 rounded-sm transform translate-y-2 group-hover:translate-y-0 transition-transform duration-500 ease-out shadow-2xl">
              <div className="flex items-center gap-4 mb-4">
                {mainPost.categories && mainPost.categories.length > 0 && <span className="text-xs font-mono tracking-widest text-primary uppercase">
                    {mainPost.categories[0].name}
                  </span>}
                <span className="text-xs text-secondary font-mono">
                  {mainPost.date ? new Date(mainPost.date).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric'
              }) : 'Live'}
                </span>
              </div>
              <h3 className="font-default text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight mb-4 text-foreground uppercase tracking-tight">
                {mainPost.title}
              </h3>
              <p className="text-muted-foreground font-sans text-sm sm:text-base line-clamp-2 mb-6 max-w-2xl">
                {mainPost.excerpt?.replace(/(<([^>]+)>)/gi, "") || "Exploring the intersection of high-performance engineering and strategic digital communication."}
              </p>
              <Link to={mainPost.link || "#"}>
                <Button variant="default" className="rounded-sm px-8 group/btn bg-primary hover:bg-primary/90 text-primary-foreground font-default uppercase tracking-wider">
                  Read Entry 
                  <ArrowRight className="ml-2 size-4 transition-transform group-hover/btn:translate-x-1" />
                </Button>
              </Link>
            </div>
          </div>
        </article>}

      {}
      <div className="lg:col-span-5 flex flex-col gap-8">
        {secondaryPosts.map((post, i) => <article key={post.id} data-index={i} className="group flex flex-col sm:flex-row lg:flex-col xl:flex-row gap-6 h-full bg-card/20 border border-border/20 rounded-sm p-4 hover:bg-card/40 transition-all duration-500">
            <div className="w-full sm:w-2/5 lg:w-full xl:w-2/5 h-48 sm:h-full lg:h-64 xl:h-full rounded-sm overflow-hidden shrink-0 relative">
              <img src={post.featuredImage || `/img/magazine_secondary_${i + 1}.png`} alt={post.title} className="absolute inset-0 w-full h-full object-cover grayscale-[0.8] group-hover:grayscale-0 transition-all duration-1000 ease-in-out scale-100 group-hover:scale-110" />
            </div>
            <div className="flex flex-col justify-center py-2 flex-1">
              <div className="flex items-center gap-3 mb-3">
                {post.categories && post.categories.length > 0 && <span className="text-[10px] font-mono tracking-widest text-primary uppercase">
                    {post.categories[0].name}
                  </span>}
              </div>
              <h4 className="font-default text-xl sm:text-2xl font-semibold leading-snug mb-3 text-foreground group-hover:text-primary transition-colors duration-300 uppercase">
                {post.title}
              </h4>
              <p className="text-muted-foreground font-sans text-sm line-clamp-2 mb-4">
                {post.excerpt?.replace(/(<([^>]+)>)/gi, "") || "Analyzing the latest shifts in the automotive market and digital landscape."}
              </p>
              <div className="mt-auto">
                <Link to={post.link || "#"}>
                  <Button variant="link" className="p-0 h-auto text-foreground hover:text-primary font-default uppercase tracking-widest text-xs">
                    View Dispatch <ArrowRight className="ml-2 size-4" />
                  </Button>
                </Link>
              </div>
            </div>
          </article>)}
      </div>
    </div>;
}
export default function PulseOfTheStudio() {
  const handleFullArchive = () => {};
  const wp_query_params = {
    per_page: 3,
    orderby: "date",
    order: "desc"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section id="the-automotive-feed" className="dark bg-background text-foreground py-24 lg:py-32 relative overflow-hidden">
      {}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[500px] bg-primary/5 blur-[150px] rounded-full pointer-events-none" />
      
      <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl relative z-10">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12 lg:mb-16 border-b border-border/20 pb-8">
          <div className="max-w-2xl">
            <h2 className="font-default text-4xl md:text-5xl lg:text-7xl font-bold tracking-tighter mb-4 uppercase italic">
              The Automotive Feed
            </h2>
            <p className="text-secondary font-sans text-lg md:text-xl font-light tracking-wide">
              Dispatches from the intersection of high-performance marketing and automotive passion.
            </p>
          </div>
          <Link to="#">
            <Button variant="outline" onClick={handleFullArchive} className="rounded-sm border-primary/40 hover:bg-primary hover:text-primary-foreground hover:border-primary transition-all font-default uppercase tracking-widest px-8">
              Full Archive
            </Button>
          </Link>
        </div>

        <PostsProvider wp_query={{
        per_page: 3,
        orderby: "date",
        order: "desc"
      }}>
          <PulsePostsConsumer />
        </PostsProvider>
      </div>
    </section>;
}